import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * Map of `item_def_id` → resolved item name. Used by tables that historically
 * displayed `did_…` strings (e.g. AdminGameDetailPage's reward rules table)
 * so the UI can render the human name instead.
 *
 * Uses the per-item endpoint and `useQueries` so individual item lookups
 * cache independently with a 5-minute staleTime — this dataset is tiny and
 * effectively static, so cache hits are the common case.
 *
 * `dedupedIds` and the `queries` config are memoized on a join-key so a
 * stable input from the caller doesn't churn `useQueries` on every render
 * (review C1 — repeated re-creation of the queries config).
 */
export function useItemDefsByIds(ids: readonly string[]): {
  byId: Record<string, string>;
} {
  const dedupedIds = useMemo(() => Array.from(new Set(ids)).sort(), [ids.join(',')]);

  const queries = useQueries({
    queries: dedupedIds.map((id) => ({
      queryKey: queryKeys.dfe.definition(id),
      queryFn: () => api.getDfeDefinition(id),
      staleTime: 5 * 60 * 1000,
      enabled: id.length > 0,
    })),
  });

  // Memoize the returned map so consumers using it as a useEffect/useMemo dep
  // don't fire on identity-only changes.
  return useMemo(() => {
    const byId: Record<string, string> = {};
    for (let i = 0; i < dedupedIds.length; i++) {
      const data = queries[i]?.data;
      if (data) byId[dedupedIds[i]] = data.name;
    }
    return { byId };
    // queries is a fresh array each render but its contents stabilize via
    // React Query's structural sharing on data.
  }, [dedupedIds, ...queries.map((q) => q.data?.name)]);
}
